/**
 * Platform-roles React Query hooks (for /api/admin/roles).
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { Role } from '../types/access';
import {
  createPlatformRole,
  listPlatformRoles,
  type CreatePlatformRoleInput,
  type PlatformRoleFilters,
} from '../api/platform-roles';

const platformRoleKeys = {
  all: ['platformRoles'] as const,
  list: (filters?: PlatformRoleFilters) => [...platformRoleKeys.all, 'list', filters] as const,
};

export const usePlatformRoles = (
  filters?: PlatformRoleFilters,
  options?: Omit<UseQueryOptions<Role[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: platformRoleKeys.list(filters),
    queryFn: () => listPlatformRoles(filters),
    ...options,
  });
};

export const useCreatePlatformRole = (
  options?: Omit<
    UseMutationOptions<Role, Error, CreatePlatformRoleInput>,
    'mutationFn' | 'onSuccess'
  > & {
    onSuccess?: (data: Role, variables: CreatePlatformRoleInput, context: unknown) => void;
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreatePlatformRoleInput) => createPlatformRole(dto),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: platformRoleKeys.all });
      options?.onSuccess?.(data, variables, context);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
};
